import * as React from "react";
import { useState } from "react";
import { devices, TranslationSteps } from "~/Common/TranslationConstant";
import { Dictionary } from "~/Types/Common";
import { ChooseLanguage } from "~/Components/ChooseLanguage";
import { EditTranslationsStep } from "~/Components/EditTranslationsStep";
import { GenerateTranslationsProgressBar } from "~/Components/GenerateTranslationsProgressBar";
import { TranslationAction } from "~/Components/TranslationAction";
import { useGetLanguages } from "~/Hooks/Api/Languages";
import { useGetPersonas } from "~/Hooks/Api/Personas";
import { useSaveContentValue, useTranslationSingleValue } from "~/Hooks/Api/Translations";
import { Content, TranslateSingleValue } from "~/Types/ApiModels";
import { react2Angular } from "~/Core/React2Angular";
import { LoadingSpinner } from "./LoadingSpinner";
import { SimpleModal } from "./SimpleModal";
import { TS } from "~/Services/TS";
export const GenerateTranslationModal = ({
    currentUserId,
    isModalOpen,
    languageId,
    contentManagerId,
    htmlContent,
    onCloseModal,
    onReloadPage,
}: {
    currentUserId: string;
    isModalOpen: boolean;
    languageId: string;
    contentManagerId: string;
    htmlContent: string;
    onCloseModal: () => void;
    onReloadPage: () => void;
}) => {
    const { data: languages } = useGetLanguages();
    const { data: personas } = useGetPersonas();
    const translationSingleMutate = useTranslationSingleValue();
    const saveContentValueMutate = useSaveContentValue();
    const currentLanguageLookups = languages ? languages.value.filter(x => x.id !== languageId) : [];
    const personaLookups = personas
        ? personas.value
              .filter(x => {
                  return x.deactivateOn === null || Date.parse(new Date().toDateString()) < Date.parse(x.deactivateOn);
              })
              .sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
        : [];
    const defaultId = personaLookups.find(x => x.isDefault)?.id;
    const [currentStep, setCurrentStep] = useState<string>(TranslationSteps.CHOOSE_LANGUAGE);
    const [stepIndex, setStepIndex] = React.useState<number>(0);
    const [selectedLanguageCodes, setSelectedLanguages] = useState<string[]>([]);
    const [isShowSaveButton, setShowSaveButton] = useState<boolean>(false);
    const [languageSelected, setLanguageSelected] = useState<string>("");
    const [translationValues, setTranslationValue] = useState<Dictionary<Content>>({});
    const [editorRef, setEditorRef] = useState<any>(null);
    const [isTranslating, setIsTranslating] = useState<boolean>(false);
    const [isDisableNextButton, setDisableNextButton] = useState<boolean>(false);
    const [isDisableSaveButton, setDisableSaveButton] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const handleCloseModal = () => {
        resetState();
        onCloseModal();
    };
    const onSelectLanguage = (languageCode: string) => {
        const currentSelectedLanguages = selectedLanguageCodes;
        const findIdx = currentSelectedLanguages.indexOf(languageCode);
        if (findIdx > -1) {
            currentSelectedLanguages.splice(findIdx, 1);
        } else {
            currentSelectedLanguages.push(languageCode);
        }
        setSelectedLanguages([...currentSelectedLanguages]);
    };
    const onHandleContinue = () => {
        setIsTranslating(true);
        setDisableNextButton(true);
        setDisableSaveButton(true);
        const sourceLanguageCode = languages?.value.find(x => x.id == languageId)?.languageCode;
        if (sourceLanguageCode) {
            const paramRequest: TranslateSingleValue = {
                originalValue: htmlContent,
                sourceLanguage: sourceLanguageCode,
                targetLanguages: selectedLanguageCodes,
            };
            translationSingleMutate.mutate(paramRequest, {
                onSuccess: response => {
                    if (response.isSuccess) {
                        const contentDictionary: Dictionary<Content> = {};
                        const { translationValues } = response.value!;
                        Object.keys(translationValues).map(key => {
                            const languageId = currentLanguageLookups.find(x => x.languageCode === key)?.id;
                            if (languageId) {
                                var value = translationValues[key] ?? "";
                                const content: Content = {
                                    languageId: languageId,
                                    personaId: defaultId ?? "",
                                    deviceType: devices[0],
                                    contentManagerId: contentManagerId,
                                    createdByAdminUserProfileId: currentUserId,
                                    html: value,
                                    revision: 1,
                                    name: "New Revision",
                                    generationType: "Manual",
                                };
                                contentDictionary[key] = content;
                            }
                        });
                        setTranslationValue({ ...contentDictionary });
                        setIsTranslating(false);
                        setDisableNextButton(false);
                        setDisableSaveButton(false);
                        if (Object.keys(contentDictionary)?.length === 1) {
                            setShowSaveButton(true);
                        }
                        setLanguageSelected(selectedLanguageCodes[stepIndex]);
                    } else {
                        if (response?.value?.errorMessage) {
                            setErrorMessage(response?.value?.errorMessage);
                        }
                    }
                },
            });
        }
        setCurrentStep(TranslationSteps.EDIT_TRANSLATION);
    };
    const onNextButton = () => {
        setValueTranslationContent();
        if (stepIndex === selectedLanguageCodes.length - 2) {
            setShowSaveButton(true);
        }
        setStepIndex(stepIndex + 1);
        setLanguageSelected(selectedLanguageCodes[stepIndex + 1]);
    };
    const onBackButton = () => {
        setShowSaveButton(false);
        setValueTranslationContent();
        if (stepIndex - 1 < 0) {
            setCurrentStep(TranslationSteps.CHOOSE_LANGUAGE);
            setStepIndex(0);
            setTranslationValue({});
        } else {
            setStepIndex(stepIndex - 1);
            setLanguageSelected(selectedLanguageCodes[stepIndex - 1]);
        }
    };
    const onSaveButton = () => {
        setValueTranslationContent();
        const languageSelectedId = currentLanguageLookups.find(x => x.languageCode === languageSelected)?.id;
        if (languageSelectedId) {
            Object.keys(translationValues).map(language => {
                const paramRequest: Content = translationValues[language]!;
                saveContentValueMutate.mutate(paramRequest, {
                    onSuccess: data => {
                        onReloadPage();
                        handleCloseModal();
                    },
                });
                setDisableSaveButton(true);
            });
        }
    };
    const onChangeDevice = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        const content = translationValues[languageSelected];
        if (content && Object.hasOwn(content, "deviceType")) {
            content!.deviceType = value;
            setTranslationValue({ ...translationValues, [languageSelected]: content });
        }
    };
    const onChangeSegment = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        const content = translationValues[languageSelected];
        if (content && Object.hasOwn(content, "personaId")) {
            content!.personaId = value;
            setTranslationValue({ ...translationValues, [languageSelected]: content });
        }
    };
    const onChangeLanguage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setValueTranslationContent();
        setLanguageSelected(event.target.value);
        const findLanguageIndex = selectedLanguageCodes.findIndex(x => x === event.target.value);
        if (findLanguageIndex === selectedLanguageCodes.length - 1) {
            setShowSaveButton(true);
        } else {
            setShowSaveButton(false);
        }
        setStepIndex(findLanguageIndex);
    };
    const resetState = () => {
        setCurrentStep(TranslationSteps.CHOOSE_LANGUAGE);
        setStepIndex(0);
        setSelectedLanguages([]);
        setShowSaveButton(false);
        setLanguageSelected("");
        setTranslationValue({});
        setEditorRef(null);
        setIsTranslating(false);
        setDisableNextButton(false);
        setDisableSaveButton(false);
        setErrorMessage("");
    };
    const setValueTranslationContent = () => {
        const value = editorRef.getData();
        const content = translationValues[languageSelected];
        if (content && Object.hasOwn(content, "html")) {
            content!.html = value;
            setTranslationValue({ ...translationValues, [languageSelected]: content });
        }
    };
    const onCloseErrorModal = () => {
        setErrorMessage("");
        setIsTranslating(false);
    };
    return (
        <div>
            <div
                className={`modal-overlay modal--large content-manager-generate-translation-modal ${isModalOpen && "is-active"}`}
                zf-modal="autoTranslateModal"
                zf-closable="modal"
                id="autoTranslateModal"
            >
                <aside className="modal">
                    <div>
                        <div className="modal-header">
                            <h4 className="modal-title">{TS("Generate Translation")} </h4>
                            <button className="close-button" onClick={handleCloseModal}>
                                <i className="fa-light fa-xmark"></i>
                            </button>
                        </div>
                        <div className="modal-content container">
                            <GenerateTranslationsProgressBar
                                isChoosingLanguage={currentStep === TranslationSteps.EDIT_TRANSLATION}
                            />
                            {isTranslating && <LoadingSpinner />}
                            {currentStep === TranslationSteps.CHOOSE_LANGUAGE && (
                                <ChooseLanguage
                                    currentLanguageLookups={currentLanguageLookups}
                                    selectedLanguageCodes={selectedLanguageCodes}
                                    onSelectLanguage={onSelectLanguage}
                                />
                            )}
                            {currentStep === TranslationSteps.EDIT_TRANSLATION && (
                                <EditTranslationsStep
                                    currentLanguageLookups={currentLanguageLookups}
                                    selectedLanguageCodes={selectedLanguageCodes}
                                    personaLookups={personaLookups}
                                    htmlContent={translationValues[selectedLanguageCodes[stepIndex]]?.html ?? ""}
                                    deviceSelected={translationValues[languageSelected]?.deviceType}
                                    languageSelected={languageSelected}
                                    segmentSelected={translationValues[languageSelected]?.personaId}
                                    onChangeDevice={onChangeDevice}
                                    onChangeSegment={onChangeSegment}
                                    onChangeLanguage={onChangeLanguage}
                                    editorRef={editorRef}
                                    setEditorRef={setEditorRef}
                                />
                            )}
                        </div>
                        <div className="modal-footer">
                            <TranslationAction
                                isDisableContinue={selectedLanguageCodes.length === 0}
                                onBackButton={onBackButton}
                                onHandleContinue={onHandleContinue}
                                onNextButton={onNextButton}
                                handleCloseModal={handleCloseModal}
                                onSaveButton={onSaveButton}
                                currentStep={currentStep}
                                isShowSaveButton={isShowSaveButton}
                                isDisableNextButton={isDisableNextButton}
                                isDisableSaveButton={isDisableSaveButton}
                            />
                        </div>
                    </div>
                </aside>
            </div>
            <SimpleModal
                isModalOpen={errorMessage !== ""}
                title={TS("Error")}
                onCloseModal={onCloseErrorModal}
                message={errorMessage}
            />
        </div>
    );
};

angular
    .module("insite-admin")
    .component(
        "generateTranslationModal",
        react2Angular(GenerateTranslationModal, [
            "currentUserId",
            "isModalOpen",
            "languageId",
            "contentManagerId",
            "htmlContent",
            "onCloseModal",
            "onReloadPage",
        ]),
    );
